import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { last, upperFirst } from 'es-toolkit/compat';
import useDocumentTitle from '@rio-cloud/rio-uikit/useDocumentTitle';
import { isMobile, isMac } from '@rio-cloud/rio-uikit/DeviceUtils';
import type { ListMenuItem } from '@rio-cloud/rio-uikit/components/listMenu/ListMenuGroup';
import ListMenu from '@rio-cloud/rio-uikit/ListMenu';

import { useUikitContext } from '../UikitContext';
import { routes } from '../routes';

const TITLE_PREFIX = 'RIO UIKIT - ';

// change document title for active page
const useTitle = (pathname: string) => {
    const title = last(pathname.split('/')) ?? '';
    useDocumentTitle(`${TITLE_PREFIX}${upperFirst(title)}`);
};

const withoutMenuBadge = (menu: ListMenuItem[]) => menu.map(item => ({ ...item, badge: undefined }));

export const UikitMenu = () => {
    const [filterValue, setFilterValue] = useState('');

    const { pathname } = useLocation();

    const { startMenu, foundationsMenu, componentsMenu, uiTemplateMenu, utilitiesMenu, internalMenu } =
        useUikitContext();

    useTitle(pathname);

    const isDesktop = !isMobile();

    const isMacOs = isMac();

    const items = useMemo(() => {
        // No filter: select menu based on pathname
        if (!filterValue) {
            if (pathname.startsWith(`/${routes.START}`)) {
                return withoutMenuBadge(startMenu);
            }
            if (pathname.startsWith(`/${routes.COMPONENTS}`)) {
                return withoutMenuBadge(componentsMenu);
            }
            if (pathname.startsWith(`/${routes.FOUNDATIONS}`)) {
                return withoutMenuBadge(foundationsMenu);
            }
            if (pathname.startsWith(`/${routes.TEMPLATES}`)) {
                return withoutMenuBadge(uiTemplateMenu);
            }
            if (pathname.startsWith(`/${routes.UTILITIES}`)) {
                return withoutMenuBadge(utilitiesMenu);
            }
            if (pathname.startsWith(`/${routes.INTERNAL}`)) {
                return withoutMenuBadge(internalMenu);
            }
            return [];
        }

        // With filter: merge all menu items
        return [...startMenu, ...foundationsMenu, ...componentsMenu, ...uiTemplateMenu, ...utilitiesMenu];
    }, [filterValue, pathname, startMenu, foundationsMenu, componentsMenu, uiTemplateMenu, utilitiesMenu]);

    return (
        <ListMenu
            className='padding-bottom-25'
            menuItems={items}
            enableFilter
            focusFilter
            filterKey='meta'
            filterPlaceholder='Filter by name'
            onFilterChange={setFilterValue}
            notFoundMessage='Nothing found'
            responsive={false}
            trailingInputAddon={
                isDesktop && (
                    <div className='input-group-addon padding-right-5'>
                        <kbd className='text-normal'>{isMacOs ? '⌘' : 'ctrl'}</kbd>
                        <kbd className='text-normal'>k</kbd>
                    </div>
                )
            }
        />
    );
};
